import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';
import { useLuminarySceneSelector, useLuminarySelector } from 'js/src/luminary/luminary-hooks';
import type { OverlaySceneDesignDefaultsByTemplateId } from 'js/src/luminary/luminary-selectors';
import {
    getSceneNumberWithOverlayDefaults,
    getOverlayRelevantTemplateIds,
} from 'js/src/luminary/luminary-selectors';

export function useOverlaySceneDesignDefaults(
    events?: string[],
    shouldUpdate?: (payload: unknown) => boolean,
): OverlaySceneDesignDefaultsByTemplateId {
    const templateIds = useLuminarySelector(
        (luminary) => getOverlayRelevantTemplateIds(luminary),
        [],
        events ? { events, shouldUpdate } : undefined,
    );

    const defaults = useSelector(
        (state: RootState) =>
            templateIds.flatMap((templateId) => [
                getTemplateDefaultTalkingHeadSceneDesign(state, templateId),
                getTemplateDefaultVoiceoverSceneDesign(state, templateId),
            ]),
        shallowEqual,
    );

    return useMemo(() => {
        const defaultsByTemplateId: OverlaySceneDesignDefaultsByTemplateId = {};
        templateIds.forEach((templateId, index) => {
            defaultsByTemplateId[templateId] = {
                defaultTalkingHeadSceneDesignId: defaults[index * 2],
                defaultVoiceoverSceneDesignId: defaults[index * 2 + 1],
            };
        });
        return defaultsByTemplateId;
    }, [templateIds, defaults]);
}

export function useSceneNumber(
    sceneId: string | null | undefined,
): string | number | null | undefined {
    const defaultsByTemplateId = useOverlaySceneDesignDefaults();

    return useLuminarySceneSelector(
        (scene) => getSceneNumberWithOverlayDefaults(scene, defaultsByTemplateId),
        [defaultsByTemplateId],
        { sceneId: sceneId ?? null, returnUndefinedIfMissing: true },
    );
}
